// @ts-check
import _transform from 'lodash/transform';
import _pick from 'lodash/pick';
import _sortBy from 'lodash/sortBy';
import _zip from 'lodash/zip';
import _isEmpty from 'lodash/isEmpty';
import { formatInTimeZone } from 'date-fns-tz';

import {
  PACKAGING_TYPES,
  PRICE_ADJUST,
  GIFT_PACKAGING_PRICE,
  LOYALTY_CARD_LABELS,
  LOYALTY_CARD_TYPES,
  DELIVERY_LINE_ITEM,
  GIFT_WRAP_LINE_ITEM,
  LINE_ITEM_TYPE,
  POS_LINE_ITEM_DEFAULT,
  BG_TIMEZONE,
} from 'constants/index';
import { formatNumberToUSD } from 'utils/numberFormatter';
import { phoneToNumber, convertToShortType, formatDateWithoutSlashMMDDYYYY } from 'utils';
import {
  mapCardRequest,
  mapGiftCardRequest,
  mapVoidCardRequest,
  mapVoidGiftCardRequest,
} from './mapPaymentSubmitRequest';

const keyMapping = {
  shortDescription: 'name',
  colorShortDescription: 'shortColor',
  colorDescription: 'color',
  sizeShortDescription: 'size',
};

const requiredTaxes = [
  'orderTotalTax',
  'orderStateTax',
  'orderCityTax',
  'orderCountyTax',
  'orderCountryTax',
  'orderDistrictTax',
  'orderSecondaryStateTax',
  'orderSecondaryCountyTax',
  'orderSecondaryCityTax',
];

const DEFAULT_GIFT_CARD_PIN = '0000';
const DELIVERY_SKU_ID = '000000000000';
const ASSOCIATE_PIN_REGEX = /^([0-9]{5,6})?$/;

/**
 * @typedef {import('../types').ProductDTO} ProductDTO
 * @typedef {import('../types').PaymentDTO} PaymentDTO
 * @typedef {import('../types').OrderGiftCard} OrderGiftCard
 * @typedef {import('../types').ShippingAddressDTO} ShippingAddressDTO
 * @typedef {import('../types').ShippingOption} ShippingOption
 * @typedef {import('../types').ShippingItem} ShippingItem
 * @typedef {import('../types').MappedLineItem} MappedLineItem
 * @typedef {import('../types').CombinedProductData} CombinedProductData
 * @typedef {import('../types').GiftCardDTO} GiftCardDTO
 * @typedef {import('../types').SubmitPaymentResponse} SubmitPaymentResponse
 * @typedef {import('../types').SubmitPaymentErrorResponse} SubmitPaymentErrorResponse
 * @typedef {import('../types').SubmitPaymentParams} SubmitPaymentParams
 * @typedef {import('../types').SubmitVoidParams} SubmitVoidParams
 * @typedef {import('../types').VoidCardPaymentRequest} VoidCardPaymentRequest
 * @typedef {import('../types').VoidGiftCardPaymentRequest} VoidGiftCardPaymentRequest
 * @typedef {import('../types').CardPaymentRequest} CardPaymentRequest
 * @typedef {import('../types').GiftCardPaymentRequest} GiftCardPaymentRequest
 * @typedef {import('../types').GroupedPaymentsResponses} GroupedPaymentsResponses
 * @typedef {import('../types').GroupedGiftCardResponses} GroupedGiftCardResponses
 * @typedef {import('../contexts/CartContext').ProductMetaData} ProductMetaData
 */

export const mapProductProperties = (product, mapping = keyMapping) =>
  _transform(
    product,
    (result, value, key) => {
      const newKey = mapping[key] || key;
      // eslint-disable-next-line no-param-reassign
      result[newKey] = value;
    },
    {},
  );

/**
 * Calculate the subtotal price based on products and quantity Map
 * @param {ProductDTO[]} products
 * @param {Map<string, ProductMetaData>} [metaData]
 */
export const getSubtotalPrice = (products = [], metaData) => {
  if (!products.length) {
    return '0.00';
  }

  return products
    .reduce((total, { currentPrice, originalPrice, pimSkuId }) => {
      const retailPrice = parseFloat(currentPrice || originalPrice);
      const currentMetaData = metaData?.get(pimSkuId) || {};
      const qty = currentMetaData?.itemQty || 1;
      const adjustedPrice = parseFloat(currentMetaData?.adjustedPriceInfo?.adjustedPrice ?? '') || null;
      const sum = adjustedPrice ? adjustedPrice * qty : retailPrice * qty;
      return total + sum;
    }, 0)
    .toFixed(2);
};

/**
 * Validate the adjustment widget input
 * @param {string} value
 * @param {string} type
 * @param {string} price
 */
export const validatePriceAdjusterInput = (value, type, price) => {
  const decimalCount = value.split('.')[1];
  const isSameValue = type !== PRICE_ADJUST.TYPES_CHARS.PERCENT && parseFloat(value).toFixed(2) === price;
  return Number(value) <= 0 || isSameValue || decimalCount?.length > 2;
};

/**
 * find the max earned inCircleLevel information from client loyalty accounts
 * @param {Object} loyaltyAccounts
 */
export const getMaxEarnedInCircleLevel = (loyaltyAccounts = {}) => {
  return Object.values(loyaltyAccounts).reduce((acc, { earnedLevel }) => {
    const currentLevel = earnedLevel ? parseInt(earnedLevel, 10) : 0;
    return currentLevel > acc ? currentLevel : acc;
  }, 0);
};

/**
 * @param {Object} loyaltyAccounts
 * @returns {Boolean}
 */
export const checkInCircleLevel3orMore = (loyaltyAccounts) => {
  const minInCircleLevel = 3;
  const inCircleLevel = getMaxEarnedInCircleLevel(loyaltyAccounts);
  return inCircleLevel >= minInCircleLevel;
};

export const getSingleWrappingCost = (giftWrapCode = '') =>
  giftWrapCode === PACKAGING_TYPES.GIFT_PACKAGING ? GIFT_PACKAGING_PRICE : 0;

/**
 * @param {ProductDTO[]} products
 * @param {Map<string, ProductMetaData>} metaData
 * @param {Boolean} isFreeGiftWrappingEligible
 */
export const getTotalWrappingCost = (products = [], metaData, isFreeGiftWrappingEligible) => {
  if (!products.length || isFreeGiftWrappingEligible) {
    return '0.00';
  }

  return products
    .reduce((total, { pimSkuId }) => {
      const currentMetaData = metaData.get(pimSkuId);
      return total + getSingleWrappingCost(currentMetaData?.giftOptions?.giftWrapCode);
    }, 0)
    .toFixed(2);
};

/**
 * Extends products data with itemQty, adjustedPrice and commissionPins from metaData.
 * If there is no commissionPins on the metaData use associatePin.
 * @param {ProductDTO[]} products
 * @param {Map<string, ProductMetaData>} metaData
 * @param {String} associatePin
 */
export const getCombinedProductsData = (products = [], metaData, associatePin) => {
  return products.map((item) => {
    const { pimSkuId } = item;
    const currentItemMeta = metaData.get(pimSkuId) || {};
    const { itemQty = 1, adjustedPriceInfo = {}, giftOptions = {}, associatePins = [] } = currentItemMeta;
    /** @type {{ adjustedPrice?: string, overrideType?: string, overridePriceDifference?: string, overridePricePercentage?: { [x: string]: string } }} */
    const { adjustedPrice, overrideType, overridePriceDifference, overridePricePercentage } = adjustedPriceInfo;
    const allCommissionPins = associatePins.length
      ? associatePins.filter(({ value }) => value).map(({ pin }) => pin)
      : associatePins;
    const commissionPins = !allCommissionPins.length ? [associatePin] : allCommissionPins;

    return {
      ...item,
      itemQty,
      adjustedPrice,
      overrideType,
      overridePriceDifference,
      overridePricePercentage,
      giftOptions,
      commissionPins,
    };
  });
};

export const mapProductsDataForPED = (products = []) => {
  const mappedProducts = products.map((item) => {
    return {
      productId: item.pimSkuId,
      productName: item.name,
      productShortDescription: item.name,
      productStatementDescriptor: item.name,
      departmentId: item.department,
      departmentName: '',
      quantity: item.itemQty,
      price: parseFloat(item.currentPrice),
      productTypeFlag: '',
    };
  });

  mappedProducts.sort((a, b) => {
    return b.price * b.quantity - a.price * a.quantity;
  });

  return mappedProducts;
};

export const mapPEDPaymentData = ({ profileData, products, orderNumber, associatePin, sledValue, shipmentData }) => {
  const { firstName, lastName } = profileData;
  const currentDate = new Date();
  const date = formatDateWithoutSlashMMDDYYYY(currentDate);
  const time = formatInTimeZone(Date.now(), BG_TIMEZONE, 'HHmmss');

  const productData = mapProductsDataForPED(products);
  const ticketData = orderNumber?.split(':');
  const ticketNumber = ticketData ? ticketData?.[1].padStart(4, '0') + ticketData?.[2].padStart(4, '0') : '';
  const { mdseTotalAmt, orderTotalAmt, orderTotalTax } = shipmentData;

  return {
    associateId: associatePin,
    apiTimeOut: '180000',
    paymentId: orderNumber,
    paymentType: 'SALE',
    transactionType: 'sale',
    sourceDeviceName: 'pos',
    destinationDeviceName: sledValue,
    destinationIP: sledValue,
    tenderType: 'CC',
    splitTransaction: 'N',
    data: {
      transRequest: {
        posId: '00733755',
        appId: '17426',
        cctId: '',
        giftPurchaseAuthIndicator: 'N',
        transactionType: '01',
        invoiceNumber: '',
        receiptNumber: '',
        customerFirstName: firstName,
        customerLastName: lastName,
        walletIdentifier: '',
        origAurusPayTicketNum: '',
        origTransactionIdentifier: '',
        currencyCode: '840',
        clerkId: associatePin,
        transactionDate: date,
        transactionTime: time,
        ticketProductData: {
          ticketCount: '1', // TODO: Not sure could be no of products
          tickets: {
            ticket: {
              ticketNumber,
              productCount: productData.length,
              products: {
                product: productData,
              },
            },
          },
        },
        transAmountDetails: {
          taxAmount: orderTotalTax,
          productTotalAmount: mdseTotalAmt,
          discount: '0.0',
          transactionTotal: `${orderTotalAmt}~${orderTotalAmt}`,
        },
      },
    },
  };
};

export const mapLoyaltyCards = (loyaltyAssets, gcPayments) => {
  const giftCardNumbers = gcPayments.map(({ giftCardNumber }) => giftCardNumber);
  const loyaltyCards = []
    .concat(...Object.values(loyaltyAssets))
    // @ts-ignore
    .filter(({ cardNumber, currentBal }) => !giftCardNumbers.includes(cardNumber.toString()) && Number(currentBal) > 0)
    .filter(({ cardType }) => cardType !== LOYALTY_CARD_TYPES.IN_CIRCLE_PERK)
    .map((card) => ({
      // @ts-ignore
      giftCardNumber: card.cardNumber.toString(),
      giftCardPin: DEFAULT_GIFT_CARD_PIN,
      cardType: LOYALTY_CARD_TYPES.NEX,
      // @ts-ignore
      giftCardBalance: parseInt(card.currentBal, 10),
      // @ts-ignore
      giftCardLabel: LOYALTY_CARD_LABELS[card.cardType],
      selected: false,
      isLoyalty: true,
      // @ts-ignore
      expDate: card.expDate,
    }));

  return [...gcPayments, ...loyaltyCards];
};

export const updateLoyaltyCardBalance = (loyaltyCard, gcPayments) => {
  return gcPayments.map((p) => {
    if (p.giftCardNumber.toString() === loyaltyCard.giftCardNumber.toString()) {
      return {
        ...p,
        giftCardBalance: loyaltyCard.giftCardBalance,
      };
    }
    return p;
  });
};

export const mapGiftWrapForReview = (lineItems = [], isFreeGiftWrappingEligible) => {
  const finalReviewItems = Array.from(lineItems);
  lineItems.forEach((lineItem) => {
    if (lineItem.giftOptions?.giftWrapCode === PACKAGING_TYPES.GIFT_PACKAGING) {
      const wrappingCost = isFreeGiftWrappingEligible ? 0 : getSingleWrappingCost(PACKAGING_TYPES.GIFT_PACKAGING);
      const giftWrapItem = Object.assign({}, GIFT_WRAP_LINE_ITEM, {
        pimSkuId: DELIVERY_SKU_ID,
        currentPrice: wrappingCost.toFixed(2),
        lineItemType: LINE_ITEM_TYPE.GIFT_WRAP,
        itemQty: 1,
        linkedPimSku: lineItem.pimSkuId,
        productCode: lineItem.productCode,
      });
      finalReviewItems.push(giftWrapItem);
    }
  });
  return finalReviewItems;
};

export const mapProductsForPOSReview = (products = [], isFreeGiftWrappingEligible = false) => {
  const finalReviewItems = mapGiftWrapForReview(products, isFreeGiftWrappingEligible);
  return finalReviewItems.map((item) => {
    const { pimSkuId, itemQty, adjustedPrice, currentPrice, department, subclass } = item;
    const price = adjustedPrice || currentPrice;
    return {
      pimSku: pimSkuId,
      quantity: itemQty,
      priceEach: price,
      departmentCode: department || item.departmentCode,
      subclass,
      class: item.class,
      lineItemType: item.lineItemType || LINE_ITEM_TYPE.PRODUCT,
      linkedPimSku: item.linkedPimSku || null,
      productCode: item.productCode || null,
    };
  });
};

export const mapShipmentMethods = (shipmentOptions) => {
  return shipmentOptions
    .map((option) => {
      const { shipmentOption, shipmentName, deliveryCharges, deliveryWindow } = option;
      return {
        id: shipmentOption,
        name: shipmentName,
        term: deliveryWindow,
        deliveryCharges: formatNumberToUSD(deliveryCharges),
      };
    })
    .sort((a, b) => (a.id > b.id ? 1 : -1));
};

/**
 * @param {ShippingOption} option
 * @param {string} giftPackagingCost
 * @param {GiftCardDTO[]} selectedGCPayments
 */
export const mapShipmentData = (option, giftPackagingCost = '0.00', selectedGCPayments = []) => {
  if (!option) return {};
  const { deliveryCharges, mdseTotalAmt, orderTotalAmt, orderTotalTax } = option;

  const fullOrderTotalAmt = orderTotalAmt + Number(giftPackagingCost) + deliveryCharges;
  // We use selectedBalance if it exists, giftCardBalance otherwise
  const totalCardDiscount = +selectedGCPayments.reduce(
    (total, payment) => total + +(payment.selectedBalance || payment.giftCardBalance || 0),
    0,
  );
  const giftCardDiscount = fullOrderTotalAmt > totalCardDiscount ? totalCardDiscount : fullOrderTotalAmt;
  const orderTotalWithGiftCard = fullOrderTotalAmt - giftCardDiscount;

  return {
    orderTotalAmt: formatNumberToUSD(fullOrderTotalAmt),
    orderTotalWithGiftCard: formatNumberToUSD(orderTotalWithGiftCard),
    giftCard: formatNumberToUSD(giftCardDiscount),
    orderTotalTax: formatNumberToUSD(orderTotalTax),
    deliveryCharges: formatNumberToUSD(deliveryCharges),
    subTotalPrice: formatNumberToUSD(mdseTotalAmt),
    giftPackaging: `$${giftPackagingCost}`,
  };
};

/** @param {ShippingItem} item */
export const getTaxOfShippingItem = (item) => item.tax;

/**
 * Converts product with shipping to lineItem
 * @param {CombinedProductData} product
 * @param {ShippingItem} shippingItem
 * @param {boolean} isOrderWrappable
 * @returns {MappedLineItem}
 */
const mapProductToLineItem = (product, shippingItem, isOrderWrappable = false) => {
  const productFields = [
    'brandId',
    'brandName',
    'class',
    'subclass',
    'color',
    'shortColor',
    'size',
    'styleId',
    'division',
  ];
  const otherFields = ['upc', 'overriddenPrice', 'emplDiscRate', 'maxDiscPct', 'supplier'];
  const {
    adjustedPrice,
    overrideType,
    overridePriceDifference,
    overridePricePercentage,
    currentPrice,
    originalPrice,
    giftOptions,
    commissionPins,
    name: productName,
    pimSkuId: pimSku,
    itemQty: quantity,
  } = product;
  const retailPrice = parseFloat(currentPrice || originalPrice || '0').toFixed(2);
  const itemPriceTotal = (parseFloat(adjustedPrice || retailPrice) * quantity).toFixed(2);
  const itemPriceEach = retailPrice;
  let adjustInformations = {};

  if (adjustedPrice) {
    adjustInformations = { overrideType, overridePriceDifference, ...overridePricePercentage };
  }

  return Object.assign({}, POS_LINE_ITEM_DEFAULT, adjustInformations, {
    ..._pick(product, productFields),
    retailPrice,
    originalPrice,
    itemPriceEach,
    itemPriceTotal,
    pimSku,
    productName,
    quantity,
    commissionPins,
    itemTaxEach: shippingItem.tax.total,
    freightEach: shippingItem.freight,
    otherEach: shippingItem.other || '0.00',
    adjustedPriceEach: adjustedPrice || retailPrice,
    productImageUrl: product.imageUrl || '',
    designerName: product.brandName,
    departmentCode: product.department,
    isIce: product.isIce || false,
    isGiftWrap: product.giftOptions?.giftWrapCode === PACKAGING_TYPES.GIFT_PACKAGING,
    tax: getTaxOfShippingItem(shippingItem),
    colorId: product.colorDifferentiatorId || '',
    sizeId: product.sizeDifferentiatorId || '',
    promotionCodes: product.promotionsApplied || [],
    ..._pick(product, otherFields),
    ...(isOrderWrappable && {
      giftmessage1: giftOptions?.giftMessage1 || '',
      giftmessage2: giftOptions?.giftMessage2 || '',
      giftmessage3: giftOptions?.giftMessage3 || '',
      giftmessage4: giftOptions?.giftMessage4 || '',
      giftmessage5: giftOptions?.giftMessage5 || '',
    }),
    giftReceipt: giftOptions?.giftReceipt,
    lineItemType: LINE_ITEM_TYPE.PRODUCT,
    // @see https://neimanmarcus.atlassian.net/browse/OSP-15524 use productCode from products endpoint
    productCode: product.productCode || shippingItem.productCode,
  });
};

/**
 * @param {CombinedProductData[]} products
 * @param {ShippingOption} shipmentMethod
 * @returns {MappedLineItem[]}
 */
export const mapProductsToLineItems = (products = [], shipmentMethod) => {
  const isOrderWrappable = products.every((product) => product.isGiftWrappable);
  return products.map((product, index) => mapProductToLineItem(product, shipmentMethod.items[index], isOrderWrappable));
};

/**
 * @param {PaymentDTO} payment
 * @param {string | number} amountReceived
 */
export const transformPaymentMethod = (payment, amountReceived) => ({
  cardType: convertToShortType(payment.networkType),
  keyIdentifier: payment.keyIdentifier,
  expiryMonth: Number(payment.cardExpMonth) || 0,
  expiryYear: Number(payment.cardExpYear) || 0,
  billingAddress: {
    ...payment.billingAddress,
    phone: [
      {
        type: 'DayPhone',
        number: phoneToNumber(payment.billingAddress.phone),
      },
    ],
  },
  amountReceived: String(amountReceived),
});

/**
 * Collect all required taxes as strings
 * @param {ShippingOption} [shipmentMethod]
 * @returns {{ [x: string]: string }} newTaxesObject
 */
export const collectShipmentTaxesAsStrings = (shipmentMethod, taxes = requiredTaxes) => {
  if (!shipmentMethod) return {};

  return Object.keys(_pick(shipmentMethod, taxes)).reduce((acc, curr) => {
    acc[curr] = shipmentMethod[curr].toString();
    return acc;
  }, {});
};

/**
 * @param {ShippingItem} [deliveryItem]
 * @returns {MappedLineItem | undefined}
 */
export const mapDeliveryLineItem = (deliveryItem) => {
  if (!deliveryItem) return;

  return Object.assign({}, POS_LINE_ITEM_DEFAULT, DELIVERY_LINE_ITEM, {
    retailPrice: deliveryItem.price,
    originalPrice: deliveryItem.price,
    itemPriceEach: deliveryItem.price,
    itemPriceTotal: deliveryItem.price,
    itemTaxEach: deliveryItem.total,
    freightEach: Number(deliveryItem.freight).toFixed(2),
    adjustedPriceEach: deliveryItem.price,
    otherEach: deliveryItem.other || '0.00',
    pimSku: deliveryItem.skuId || DELIVERY_SKU_ID,
    tax: getTaxOfShippingItem(deliveryItem),
    lineItemType: deliveryItem.lineItemType,
    productCode: deliveryItem.productCode,
    productName: 'Delivery',
  });
};

/**
 * @param {ShippingItem} [giftWrapItem]
 * @returns {MappedLineItem | undefined}
 *
 */
export const mapGiftWrapLineItem = (giftWrapItem) => {
  if (!giftWrapItem) return;

  return Object.assign({}, POS_LINE_ITEM_DEFAULT, GIFT_WRAP_LINE_ITEM, {
    retailPrice: giftWrapItem.price,
    originalPrice: giftWrapItem.price,
    itemPriceEach: giftWrapItem.price,
    itemPriceTotal: giftWrapItem.price,
    itemTaxEach: giftWrapItem.total,
    freightEach: giftWrapItem.freight,
    adjustedPriceEach: giftWrapItem.price,
    otherEach: giftWrapItem.other || '0.00',
    pimSku: giftWrapItem.skuId || DELIVERY_SKU_ID,
    tax: getTaxOfShippingItem(giftWrapItem),
    lineItemType: giftWrapItem.lineItemType,
    productCode: giftWrapItem.productCode,
    linkedPimSku: giftWrapItem.linkedPimSku,
    productName: 'Gift Wrap',
  });
};

/**
 * @param {Map<string, ProductMetaData>} metaData
 */
export const cloneMetaData = (metaData) => new Map(metaData);

/**
 * @param {string} firstPin
 * @param {string} secondPin
 */
export const validatePins = (firstPin, secondPin) =>
  ASSOCIATE_PIN_REGEX.test(firstPin) && ASSOCIATE_PIN_REGEX.test(secondPin);

/**
 * Sorts the array of payments by defaultCard first
 * @param {PaymentDTO[]} paymentMethods
 */
export const sortPaymentMethodsByDefaultCard = (paymentMethods = []) =>
  _sortBy(paymentMethods, (payment) => !payment.defaultCard);

/**
 * Return the list of selectedPaymentOptions
 * @param {boolean[]} checkedOptions
 * @param {PaymentDTO[]} paymentMethods
 * @param {{ [x: string]: number }} selectedPaymentsMap
 */
export const getSelectedPaymentOptions = (checkedOptions = [], paymentMethods = [], selectedPaymentsMap = {}) => {
  if (checkedOptions.length !== paymentMethods.length) return [];

  return paymentMethods.map(({ keyIdentifier }, index) => {
    const result = {};
    const amount = selectedPaymentsMap[keyIdentifier];

    if (amount) {
      result.amount = amount;
    }

    result.selected = checkedOptions[index];

    return result;
  });
};

export const checkArraysAreIdentical = (arr1 = [], arr2 = [], comparator = ([a, b]) => a !== b) =>
  _zip(arr1, arr2).filter(comparator).length === 0;

/**
 * Return map of giftCard number and selected anount calculated by provided gift cards list and total amount
 *
 * @param {Number} totalAmount
 * @param {GiftCardDTO[]} giftCards
 * @param {Boolean[]} selectList optional selected gift cards indexes, if not provided used 'selected' field from giftCards
 * @returns {{}}
 */
export const calculateGiftCardsSelectedAmountMap = (totalAmount, giftCards, selectList = []) => {
  let remainingAmount = totalAmount;
  const giftCardsAmountsMap = {};

  giftCards.forEach(({ giftCardNumber, giftCardBalance, selected }, index) => {
    // If we have selectList than use its values as selected pointer, use selected field if gift card otherwise
    const isSelected = selectList.length ? selectList[index] : selected;

    if (!isSelected || remainingAmount < 0) {
      giftCardsAmountsMap[giftCardNumber] = 0;
      return;
    }

    const numberedBalance = Number(giftCardBalance);
    // select only necessary amount
    const selectedBalance = numberedBalance > remainingAmount ? remainingAmount : numberedBalance;
    // calculate remaining amount according to selected gift cards
    remainingAmount -= selectedBalance;
    giftCardsAmountsMap[giftCardNumber] = selectedBalance;
  });

  return giftCardsAmountsMap;
};

/**
 * Returns total amount from ids map
 * @param {Record<string, string | number>} map
 * @return {number}
 * */
export const getTotalAmountFromIdsMap = (map) => {
  return Object.keys(map).reduce((result, id) => {
    const amount = Number(map[id]) || 0;

    return result + amount;
  }, 0);
};

/**
 * Returns length of amount ids map
 * @param {Record<string, string | number>} map
 * @return {number}
 * */
export const getAmountIdsMapLength = (map) => {
  return Object.values(map).filter(Boolean).length;
};

/**
 * Returns correct ids map and the rest of entry map
 * @param {Record<string, string | number>} map
 * @param {string[]} ids
 * @return {{
 *   matches: Record<string, string | number>,
 *   rest: Record<string, string | number>
 * }}
 * */
export const splitAmountIdsMapByIds = (map, ids) => {
  const rest = { ...map };

  const matches = ids.reduce((result, id) => {
    Object.assign(result, {
      [id]: map[id] || 0,
    });

    delete rest[id];

    return result;
  }, {});

  return {
    matches,
    rest,
  };
};

/**
 * Return array of payments which keyIdentifier includes to entry map
 * @param {Record<string, string | number>} map
 * @param {PaymentDTO[]} payments
 * @return {PaymentDTO[]}
 * */
export const getPaymentsFromAmountIdsMap = (map, payments) => {
  return payments.filter((p) => map.hasOwnProperty(p.keyIdentifier) && map[p.keyIdentifier] !== 0);
};

/**
 * Concat amounts from two amount ids map
 * @param {Record<string, string | number>} rootMap
 * @param {Record<string, string | number>} mapToUpdate
 * @return {Record<string, string | number>}
 * */
export const concatAmountMaps = (rootMap, mapToUpdate) => {
  const result = { ...rootMap };

  Object.keys(result).forEach((id) => {
    const isAlreadySelected = Boolean(mapToUpdate[id]);

    if (isAlreadySelected) {
      result[id] = +rootMap[id] + +mapToUpdate[id];
    }
  });

  return { ...mapToUpdate, ...result };
};

/**
 * Filter gift card list by provided gift card numbers, inverse param regulate including or excluding
 * @param {GiftCardDTO[]} giftCards
 * @param {string[]} giftCardNumbers
 * @param {Boolean} inverse
 * @return {GiftCardDTO[] | []}
 * */
export const filterGiftCardsByNumbers = (giftCards, giftCardNumbers, inverse = false) => {
  return giftCards.filter(({ giftCardNumber }) =>
    inverse ? !giftCardNumbers.includes(giftCardNumber) : giftCardNumbers.includes(giftCardNumber),
  );
};

/**
 * Get sum of submitted credit cards amounts and gift cards
 * @param {GiftCardDTO[]} giftCards
 * @param {Record<string, number | string>} submittedPaymentsKeyIdsMap
 * @return {number}
 * */
export const getTotalSpentAmount = (giftCards, submittedPaymentsKeyIdsMap) => {
  const submittedGCAmount = giftCards.length
    ? giftCards.reduce((acc, { giftCardBalance }) => Number(acc) + Number(giftCardBalance), 0)
    : 0;

  const creditCardsMap = Object.values(submittedPaymentsKeyIdsMap);
  const submittedCCAmount = creditCardsMap.length
    ? creditCardsMap.reduce((acc, amount) => Number(acc) + Number(amount), 0)
    : 0;

  return Number(submittedGCAmount) + Number(submittedCCAmount);
};

/**
 * Map submit payment params to submit request body
 * @param {SubmitPaymentParams} params
 * @return {{
 *   cardPayloads: CardPaymentRequest[],
 *   giftCardPayloads: GiftCardPaymentRequest[]
 * }}
 */
export const getAuthPaymentsRequestPayloads = ({
  order,
  shippingAddress,
  payments = [],
  gcPayments = [],
  amountIdsMap,
  invoiceNumber,
  transactionNumber,
}) => {
  const { items, orderTotalTax = '', orderTotalAmt = '' } = order;

  const cardPayloads = payments.map((payment) => {
    /**
     * In refactored version of InstoreCheckout we don't use amountIdsMap
     * @TODO: remove after switching InstoreCheckoutV2 as main version, use only amount field
     */
    const amount = _isEmpty(amountIdsMap) ? payment.amount : amountIdsMap[payment?.keyIdentifier];
    return mapCardRequest({
      payment,
      items,
      shippingAddress,
      invoiceNumber,
      transactionNumber,
      productTotalAmount: orderTotalAmt,
      productTotalTax: orderTotalTax,
      // @ts-ignore
      transactionTotal: (amount || 0).toString(),
    });
  });

  const giftCardPayloads = gcPayments.map((giftCardData) =>
    mapGiftCardRequest({
      giftCardData,
      items,
      shippingAddress,
      invoiceNumber,
      transactionNumber,
      productTotalAmount: orderTotalAmt,
      productTotalTax: orderTotalTax,
      transactionTotal: giftCardData.selectedBalance.toString(),
      // @ts-ignore
      origAurusPayTicketNum: giftCardData?.tempTicketData?.origAurusPayTicketNum,
      // @ts-ignore
      origTransactionIdentifier: giftCardData?.tempTicketData?.origTransactionIdentifier,
    }),
  );

  return {
    cardPayloads,
    giftCardPayloads,
  };
};

/**
 * Get mapping of succeeded credit cards keyIdentifiers and spent amounts
 * @returns {Object} [{keyIdentifier: amount}]
 */
export const getSubmittedPaymentsAmountMap = (selectedPayments, failedPaymentsKeyIdentifiers = []) => {
  const result = {};

  selectedPayments.forEach(({ keyIdentifier, amount }) => {
    if (!failedPaymentsKeyIdentifiers.includes(keyIdentifier)) {
      result[keyIdentifier] = amount;
    }
  });

  return result;
};

/**
 * Map submit payment params to submit request body
 * @param {SubmitVoidParams} params
 * @return {{
 *   cardPayloads: VoidCardPaymentRequest[],
 *   giftCardPayloads: VoidGiftCardPaymentRequest[]
 * }}
 */
export const getVoidRequestPayload = ({
  order,
  shippingAddress,
  payments,
  gcPayments,
  invoiceNumber,
  transactionNumber,
}) => {
  const { items, orderTotalTax = '', orderTotalAmt = '' } = order;

  const cardPayloads = payments.map((payment) =>
    mapVoidCardRequest({
      payment,
      items,
      shippingAddress,
      invoiceNumber,
      transactionNumber,
      productTotalAmount: orderTotalAmt,
      productTotalTax: orderTotalTax,
    }),
  );

  const giftCardPayloads = gcPayments.map((payment) =>
    mapVoidGiftCardRequest({
      payment,
      items,
      shippingAddress,
      invoiceNumber,
      transactionNumber,
      productTotalAmount: orderTotalAmt,
      productTotalTax: orderTotalTax,
    }),
  );

  return {
    cardPayloads,
    giftCardPayloads,
  };
};

/**
 * Get only success payment responses
 * @param {(SubmitPaymentResponse | SubmitPaymentErrorResponse)[]} responses
 * */
const getSuccessResponses = (responses) => {
  return responses.filter((res) => !('error' in res));
};

/**
 * Group submit payment responses
 * @param {(SubmitPaymentResponse | SubmitPaymentErrorResponse)[]} responses
 * @return {GroupedPaymentsResponses}
 * */
export const groupSubmitPaymentResponses = (responses) => {
  const failedPaymentsKeyIdentifiers = responses.flatMap((res) =>
    'error' in res && res?.error?.paymentKeyIdentifier ? [res.error.paymentKeyIdentifier] : [],
  );

  return {
    // @ts-ignore
    successfulResponses: getSuccessResponses(responses),
    failedPaymentsKeyIdentifiers,
  };
};

/**
 * Group gift card submit responses
 * @param {(SubmitPaymentResponse | SubmitPaymentErrorResponse)[]} responses
 * @return {GroupedGiftCardResponses}
 * */
export const groupGiftCardResponses = (responses) => {
  const failedGiftCardNumbers = responses.flatMap((res) =>
    'error' in res && res?.error?.failedGiftCardNumber ? [res.error.failedGiftCardNumber] : [],
  );

  return {
    // @ts-ignore
    successfulResponses: getSuccessResponses(responses),
    failedGiftCardNumbers,
  };
};

/**
 * Get error message for submit payments request
 * @param {number} errorResponsesCount
 * @param {number} submittedPaymentsCount
 * @param {SubmitPaymentErrorResponse[]} responses
 * @return {string | null}
 * */
export const getSubmitPaymentsErrorMessage = (errorResponsesCount, submittedPaymentsCount, responses) => {
  if (!errorResponsesCount || !submittedPaymentsCount) {
    return null;
  }

  const defaultMessage =
    'The Card Provided could not be processed. You may contact the card issuer for more information.';

  if (submittedPaymentsCount === 1) {
    const failedResponse = responses[0];

    return failedResponse?.error?.message || defaultMessage;
  }

  return defaultMessage;
};

export const buildVoidPayments = (methods, authData, isGiftCard = false) => {
  const identifier = isGiftCard ? 'giftCardNumber' : 'keyIdentifier';

  return authData.map((auth) => {
    const paymentData = methods.find((method) => method[identifier] === auth[identifier]);
    return {
      ...auth,
      ...paymentData,
    };
  });
};

export const calculateGiftWrappingCost = (items = []) => {
  const giftWrapItemList = items.filter((item) => item.lineItemType === LINE_ITEM_TYPE.GIFT_WRAP);

  return giftWrapItemList.reduce((total, { price, quantity }) => total + quantity * price, 0);
};
